/* Photo reviews: native scrolling; autoplay pauses for interaction or reduced motion. */
(() => {
  if (customElements.get("mb-photo-reviews")) return;

  class PhotoReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-review-track]");
      this.cards = [...this.querySelectorAll("[data-review-card]")];
      if (!this.track || !this.cards.length) return;
      this.motion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.paused = this.dataset.autoplay !== "true" || this.motion.matches;
      this.visible = false;
      this.hovering = false;
      this.index = 0;
      this.abort = new AbortController();
      const listen = (target, event, handler, options = {}) => {
        target.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      };

      listen(this.track, "keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        this.go(event.key === "ArrowRight" ? 1 : -1);
      });
      listen(this.track, "pointerenter", (event) => {
        if (event.pointerType !== "mouse") return;
        this.hovering = true;
        this.updatePlayback();
      });
      listen(this.track, "pointerleave", () => {
        this.hovering = false;
        this.updatePlayback();
      });
      listen(this.track, "pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        this.touching = true;
        this.updatePlayback();
        if (event.pointerType !== "mouse") return;

        event.preventDefault();
        this.track.classList.add("is-dragging");
        this.track.scrollTo({ left: this.track.scrollLeft, behavior: "instant" });
        this.drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScroll: this.track.scrollLeft,
        };
        this.track.setPointerCapture(event.pointerId);
      });
      listen(this.track, "pointermove", (event) => {
        if (!this.drag || event.pointerId !== this.drag.pointerId) return;
        this.track.scrollLeft = this.drag.startScroll - (event.clientX - this.drag.startX);
      });
      listen(window, "pointerup", (event) => this.finishInteraction(event));
      listen(window, "pointercancel", (event) => this.finishInteraction(event));
      listen(this.track, "lostpointercapture", (event) => this.finishInteraction(event));
      listen(this.track, "dragstart", (event) => event.preventDefault());
      listen(this, "focusin", () => this.updatePlayback());
      listen(this, "focusout", () => {
        queueMicrotask(() => {
          if (this.isConnected) this.updatePlayback();
        });
      });
      listen(document, "visibilitychange", () => this.updatePlayback());
      listen(this.motion, "change", () => {
        if (this.motion.matches) this.paused = true;
        this.updatePlayback();
      });
      listen(
        this.track,
        "scroll",
        () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            const step = this.cards[1]
              ? this.cards[1].offsetLeft - this.cards[0].offsetLeft
              : this.cards[0].offsetWidth;
            this.index = Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / step));
            this.updateTrackHeight();
          });
        },
        { passive: true },
      );
      listen(this, "shopify:block:select", (event) => {
        const card = event.target.closest("[data-review-card]");
        if (!card) return;
        this.paused = true;
        this.scrollToCard(this.cards.indexOf(card));
        this.updatePlayback();
      });

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.updatePlayback();
        },
        { threshold: 0.15 },
      );
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.updateTrackHeight());
      this.resizeObserver.observe(this.track);
      this.cards.forEach((card) => this.resizeObserver.observe(card));
      this.updateTrackHeight();
      this.updatePlayback();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.drag = null;
      this.touching = false;
      this.track?.classList.remove("is-dragging");
      this.track?.style.removeProperty("height");
    }

    updateTrackHeight() {
      // Offscreen long reviews must not leave empty space below the visible cards.
      const track = this.track.getBoundingClientRect();
      let height = 0;
      this.cards.forEach((card) => {
        const bounds = card.getBoundingClientRect();
        if (bounds.right > track.left && bounds.left < track.right) height = Math.max(height, bounds.height);
      });
      if (!height) return;
      const nextHeight = `${Math.ceil(height)}px`;
      if (this.track.style.height !== nextHeight) this.track.style.height = nextHeight;
    }

    finishInteraction(event) {
      if (this.drag && event.pointerId !== this.drag.pointerId) return;
      const drag = this.drag;
      this.drag = null;
      this.touching = false;
      if (drag) {
        if (this.track.hasPointerCapture(drag.pointerId)) this.track.releasePointerCapture(drag.pointerId);
        const step = this.cards[1] ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.cards[0].offsetWidth;
        this.index = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / step)));
        this.track.classList.remove("is-dragging");
        this.scrollToCard(this.index);
      }
      this.updatePlayback();
    }

    updatePlayback() {
      clearInterval(this.timer);
      const focused = this.contains(document.activeElement);
      if (
        this.paused ||
        !this.visible ||
        this.hovering ||
        this.touching ||
        focused ||
        document.hidden ||
        window.Shopify?.designMode
      )
        return;
      this.timer = setInterval(() => this.go(1), Number(this.dataset.interval) || 5000);
    }

    scrollToCard(index, instant = false) {
      this.track.scrollTo({
        left: this.cards[index].offsetLeft - this.cards[0].offsetLeft,
        behavior: instant || this.motion.matches ? "instant" : "smooth",
      });
    }

    go(direction) {
      const end = this.track.scrollWidth - this.track.clientWidth;
      if (direction > 0 && this.track.scrollLeft >= end - 2) {
        this.scrollToCard(0, true);
      } else if (direction < 0 && this.track.scrollLeft <= 2) {
        this.scrollToCard(this.cards.length - 1, true);
      } else {
        this.scrollToCard(Math.max(0, Math.min(this.cards.length - 1, this.index + direction)));
      }
    }
  }
  customElements.define("mb-photo-reviews", PhotoReviews);
})();
